import { DI } from 'aurelia';
import { IMetadataPagination } from '../state';

export const IMetadataService = DI.createInterface<IMetadataService>();

export interface IMetadataService {
    updateMetaDescription(metaDescription: string | null): void;
    updateMetaTitle(title: string | null): void;
    updateMetaImage(image: string | null): void;
    updateMetaUrl(url: string | null): void;
    updateMetaType(type: string | null): void;
    updateMetaSiteName(siteName: string | null): void;
    updateMetaTwitterCard(card: string | null): void;
    updateMetaTwitterSite(site: string | null): void;
    updateMetaTwitterCreator(creator: string | null): void
    updateMetaPagination(metaPaginationData: IMetadataPagination, currentUrl: string): void;
    updateMetaPrerender(prerender: string | null): void;
    updateMetaRobots(robots: string | null): void;
    updateMetaThemeColor(themeColor: string | null): void;
}

export class MetadataService implements IMetadataService {
    updateMetaDescription(metaDescription: string | null) {
        if (!metaDescription) { return; };
        //Set Description
        const matches = document.querySelectorAll('meta[name="description"]');
        if (matches.length) {
            const tag = matches[0] as HTMLMetaElement;
            tag.content = metaDescription;
        } else {
            const tag = document.createElement('meta');
            tag.setAttribute('name', 'description');
            tag.content = metaDescription;
            document.getElementsByTagName('head')[0].appendChild(tag);
        }

        //Set og:description
        const ogDescriptionMatches = document.querySelectorAll('meta[property="og:description"]');
        if (ogDescriptionMatches.length) {
            const tag = ogDescriptionMatches[0] as HTMLMetaElement;
            tag.content = metaDescription;
        } else {
            const ogDescription = document.createElement('meta');
            ogDescription.setAttribute('property', 'og:description');
            ogDescription.content = metaDescription;
            document.getElementsByTagName('head')[0].appendChild(ogDescription);
        }

        // set twitter:description
        const twitterDescriptionMatches = document.querySelectorAll('meta[name="twitter:description"]');
        if (twitterDescriptionMatches.length) {
            const tag = twitterDescriptionMatches[0] as HTMLMetaElement;
            tag.content = metaDescription;
        } else {
            const twitterDescription = document.createElement('meta');
            twitterDescription.setAttribute('name', 'twitter:description');
            twitterDescription.content = metaDescription;
            document.getElementsByTagName('head')[0].appendChild(twitterDescription);
        }
    }

    updateMetaTitle(title: string | null) {
        if (!title) { return; };
        //Set Title
        const metaTitles = document.querySelectorAll('meta[name="title"]');
        if (metaTitles.length) {
            const tag = metaTitles[0] as HTMLMetaElement;
            tag.content = title;
        } else {
            const metaTitle = document.createElement('meta');
            metaTitle.setAttribute('name', 'title');
            metaTitle.content = title;
            document.getElementsByTagName('head')[0].appendChild(metaTitle);
        }

        //Set og:title
        const ogTitleMatches = document.querySelectorAll('meta[property="og:title"]');
        if (ogTitleMatches.length) {
            const tag = ogTitleMatches[0] as HTMLMetaElement;
            tag.content = `${title}`;
        } else {
            const ogTitle = document.createElement('meta');
            ogTitle.setAttribute('property', 'og:title');
            ogTitle.content = `${title}`;
            document.getElementsByTagName('head')[0].appendChild(ogTitle);
        }

        //Set twitter:title
        const twitterTitleMatches = document.querySelectorAll('meta[name="twitter:title"]');
        if (twitterTitleMatches.length) {
            const tag = twitterTitleMatches[0] as HTMLMetaElement;
            tag.content = `${title}`;
        } else {
            const twitterTitle = document.createElement('meta');
            twitterTitle.setAttribute('name', 'twitter:title');
            twitterTitle.content = `${title}`;
            document.getElementsByTagName('head')[0].appendChild(twitterTitle);
        }
    }

    updateMetaImage(image: string | null) {
        if (!image) { return; };
        //Set og:image
        const ogImageMatches = document.querySelectorAll('meta[property="og:image"]');
        if (ogImageMatches.length) {
            const tag = ogImageMatches[0] as HTMLMetaElement;
            tag.content = image;
        } else {
            const ogImage = document.createElement('meta');
            ogImage.setAttribute('property', 'og:image');
            ogImage.content = image;
            document.getElementsByTagName('head')[0].appendChild(ogImage);
        }

        //Set twitter:image
        const twitterImageMatches = document.querySelectorAll('meta[name="twitter:image"]');
        if (twitterImageMatches.length) {
            const tag = twitterImageMatches[0] as HTMLMetaElement;
            tag.content = image;
        } else {
            const twitterImage = document.createElement('meta');
            twitterImage.setAttribute('name', 'twitter:image');
            twitterImage.content = image;
            document.getElementsByTagName('head')[0].appendChild(twitterImage);
        }
    }

    updateMetaUrl(url: string | null) {
        if (!url) { return; };
        //Set og:url
        const ogUrlMatches = document.querySelectorAll('meta[property="og:url"]');
        if (ogUrlMatches.length) {
            const tag = ogUrlMatches[0] as HTMLMetaElement;
            tag.content = url;
        } else {
            const ogUrl = document.createElement('meta');
            ogUrl.setAttribute('property', 'og:url');
            ogUrl.content = url;
            document.getElementsByTagName('head')[0].appendChild(ogUrl);
        }

        //Set twitter:site
        const twitterSiteMatches = document.querySelectorAll('meta[name="twitter:site"]');
        if (twitterSiteMatches.length) {
            const tag = twitterSiteMatches[0] as HTMLMetaElement;
            tag.content = url;
        } else {
            const twitterSite = document.createElement('meta');
            twitterSite.setAttribute('name', 'twitter:site');
            twitterSite.content = url;
            document.getElementsByTagName('head')[0].appendChild(twitterSite);
        }

        //Set canonical
        const canonicalMatches = document.querySelectorAll('link[rel=canonical]');
        if (canonicalMatches.length) {
            const tag = canonicalMatches[0] as HTMLLinkElement;
            tag.href = url;
        } else {
            const canonicalUrl = document.createElement('link');
            canonicalUrl.setAttribute('rel', 'canonical');
            canonicalUrl.href = url;
            document.getElementsByTagName('head')[0].appendChild(canonicalUrl);
        }
    };

    updateMetaType(type: string | null) {
        if (!type) { return; };
        //Set og:type
        const ogTypeMatches = document.querySelectorAll('meta[property="og:type"]');
        if (ogTypeMatches.length) {
            const tag = ogTypeMatches[0] as HTMLMetaElement;
            tag.content = type;
        } else {
            const ogType = document.createElement('meta');
            ogType.setAttribute('property', 'og:type');
            ogType.content = type;
            document.getElementsByTagName('head')[0].appendChild(ogType);
        }
    }

    updateMetaSiteName(siteName: string | null) {
        if (!siteName) { return; };
        //Set og:site_name
        const ogSiteNameMatches = document.querySelectorAll('meta[property="og:site_name"]');
        if (ogSiteNameMatches.length) {
            const tag = ogSiteNameMatches[0] as HTMLMetaElement;
            tag.content = siteName;
        } else {
            const ogSiteName = document.createElement('meta');
            ogSiteName.setAttribute('property', 'og:site_name');
            ogSiteName.content = siteName;
            document.getElementsByTagName('head')[0].appendChild(ogSiteName);
        }
    }

    updateMetaTwitterCard(card: string | null) {
        if (!card) { return; };
        //Set twitter:card
        const twitterCardMatches = document.querySelectorAll('meta[name="twitter:card"]');
        if (twitterCardMatches.length) {
            const tag = twitterCardMatches[0] as HTMLMetaElement;
            tag.content = card;
        } else {
            const twitterCard = document.createElement('meta');
            twitterCard.setAttribute('name', 'twitter:card');
            twitterCard.content = card;
            document.getElementsByTagName('head')[0].appendChild(twitterCard);
        }
    }

    updateMetaTwitterSite(site: string | null) {
        if (!site) { return; };
        //Set twitter:site
        const twitterSiteMatches = document.querySelectorAll('meta[name="twitter:site"]');
        if (twitterSiteMatches.length) {
            const tag = twitterSiteMatches[0] as HTMLMetaElement;
            tag.content = site;
        } else {
            const twitterSite = document.createElement('meta');
            twitterSite.setAttribute('name', 'twitter:site');
            twitterSite.content = site;
            document.getElementsByTagName('head')[0].appendChild(twitterSite);
        }
    }

    updateMetaTwitterCreator(creator: string | null) {
        if (!creator) { return; };
        //Set twitter:creator
        const twitterCreatorMatches = document.querySelectorAll('meta[name="twitter:creator"]');
        if (twitterCreatorMatches.length) {
            const tag = twitterCreatorMatches[0] as HTMLMetaElement;
            tag.content = creator;
        } else {
            const twitterCreator = document.createElement('meta');
            twitterCreator.setAttribute('name', 'twitter:creator');
            twitterCreator.content = creator;
            document.getElementsByTagName('head')[0].appendChild(twitterCreator);
        }
    }


    updateMetaPagination(metaPaginationData: { count: number, page: number }, currentUrl: string) {
        let { count, page } = metaPaginationData;
        let url = currentUrl;

        const nextLink = document.querySelector('link[rel=next]');
        const prevLink = document.querySelector('link[rel=prev]');
        const nextPage = page + (page < count ? 1 : 0);
        const prevPage = page - (page > 1 ? 1 : 0);

        if (prevLink) {
            prevLink.remove();
        }

        if (prevPage !== page && prevPage < page && count > 0) {
            const prevHref = this.getNewHref(prevPage, url);
            const newPrevLink = document.createElement('link');
            newPrevLink.setAttribute('rel', 'prev');
            newPrevLink.setAttribute('href', prevHref);
            document.head.appendChild(newPrevLink);
        }

        if (nextLink) {
            nextLink.remove();
        }

        if (nextPage !== page && nextPage > page && count > 0) {
            const nextHref = this.getNewHref(nextPage, url);
            const newNextLink = document.createElement('link');
            newNextLink.setAttribute('rel', 'next');
            newNextLink.setAttribute('href', nextHref);
            document.head.appendChild(newNextLink);
        }
    }

    private getNewHref(page, url: string) {
        const urlSplit = url.split('/');
        urlSplit.shift();
        if (urlSplit.some(item => item === 'page')) {
            const indexPage = urlSplit.indexOf('page');
            urlSplit[indexPage + 1] = page;
            return `${window.location.href}${urlSplit.join('/')}`;
        }
        return `${window.location.origin}${urlSplit.join('/')}/page/${page}`;
    }

    updateMetaPrerender(prerender: string | null) {
        if (!prerender) { return; };
        const matches = document.querySelectorAll('meta[name="prerender-header"]');
        if (matches.length) {
            const tag = matches[0] as HTMLMetaElement;
            tag.content = prerender;
        } else {
            const tag = document.createElement('meta');
            tag.setAttribute('name', 'prerender-header');
            tag.content = prerender;
            document.getElementsByTagName('head')[0].appendChild(tag);
        }
    }

    updateMetaRobots(robots: string | null) {
        if (!robots) { return; }
        let robotsMetaTag = document.querySelector('meta[name="robots"]');
        if (!robotsMetaTag) {
            robotsMetaTag = document.createElement('meta');
            robotsMetaTag.setAttribute('name', 'robots');
        }
        robotsMetaTag.setAttribute('content', robots);
    }

    updateMetaThemeColor(themeColor: string | null) {
        if (!themeColor) { return; }
        let themeColorMetaTag = document.querySelector('meta[name="theme-color"]');
        if (!themeColorMetaTag) {
            themeColorMetaTag = document.createElement('meta');
            themeColorMetaTag.setAttribute('name', 'theme-color');
        }
        themeColorMetaTag.setAttribute('content', themeColor);
    }
}
